import { EmploymentForm } from '@/components/PageForm/PersonalDetails/Update/types';

import {
  COUNTRY_PHONE_CODES,
  COUNTRY_PHONE_CODES_ARRAY,
} from '@/constants/countryCodes';

import { Tenant } from '@/types/common';
import { EmploymentDetails, User } from '@/types/user';

import { isEmpty } from '@/utils/common';
import { getRiskSectorFlag } from '@/utils/employment';
import {
  convertToBoolean,
  emptyStringIfNull,
  getPhoneString,
} from '@/utils/string';
import { extractLocalPhoneNumber } from '@/utils/user';

export type EmploymentInfoForm = {
  employmentDetailed: EmploymentForm;
  isStartup: EmploymentDetails['isStartup'] | null;
  riskSectorFlag: User['riskSectorFlag'] | null;
};

export const getDefaultEmployerFormValues = (
  user?: User,
): EmploymentInfoForm => {
  const localPhoneNumber = extractLocalPhoneNumber(
    user?.employmentDetailed?.employerPhone || '',
  );
  const tenant = getTenantByPhoneCode(localPhoneNumber.tenant);

  return {
    employmentDetailed: {
      employmentStatus: user?.employmentDetailed?.employmentStatus || undefined,
      employerName: user?.employmentDetailed?.employerName || '',
      employerAddress: user?.employmentDetailed?.employerAddress || '',
      employerPhone: localPhoneNumber.phoneNumber || '',
      employerPhoneCode: tenant
        ? COUNTRY_PHONE_CODES[tenant]
        : COUNTRY_PHONE_CODES[user?.tenant as Tenant],
      employerCountry: user?.employmentDetailed?.employerCountry || '',
      occupationCode: user?.employmentDetailed?.occupationCode || '',
      employmentDesc: user?.employmentDetailed?.employmentDesc || '',
      startDate: user?.employmentDetailed?.startDate || '',
      riskSectorCategory: user?.employmentDetailed?.riskSectorCategory || null,
      businessCategoryCode: emptyStringIfNull(
        user?.employmentDetailed?.businessCategoryCode,
      ),
    },
    isStartup: user?.employmentDetailed?.isStartup ?? null,
    riskSectorFlag: user?.employmentDetailed?.riskSectorFlag ?? null,
  };
};

type BuildRequestPayloadParams = {
  payload: EmploymentInfoForm;
  user?: User;
};

export function buildRequestPayload({
  user,
  payload,
}: BuildRequestPayloadParams) {
  const {
    employmentDetailed: {
      employerPhone,
      employerPhoneCode,
      ...employerDetails
    } = {},
  } = payload;

  const employmentDesc = isEmpty(payload.employmentDetailed.employmentDesc)
    ? undefined
    : payload.employmentDetailed.employmentDesc;
  const isStartup = convertToBoolean(payload.isStartup);
  return {
    currentUser: user,
    employmentDetailed: {
      ...employerDetails,
      riskSectorFlag: getRiskSectorFlag(
        payload.riskSectorFlag,
        payload.employmentDetailed.riskSectorCategory,
      ),
      employmentDesc,
      isStartup,
      ...(employerDetails.employerName && {
        employerPhone: getPhoneString(
          employerPhoneCode ?? '',
          employerPhone ?? '',
        ),
      }),
    },
  };
}

export const getTenantByPhoneCode = (phoneCode: string): Tenant | undefined => {
  const entries = Object.entries(COUNTRY_PHONE_CODES_ARRAY) as [
    Tenant,
    string[],
  ][];

  return entries.find(([, codes]) => codes.includes(phoneCode))?.[0];
};
